import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * DetachedViewer — renders the resource viewer into a separate browser window.
 *
 * React port of Angular's childWindow.ts. The window MUST be opened synchronously
 * inside the click handler (openDetachedWindow) — opening it later, e.g. in an
 * effect, loses the user-gesture context and the popup blocker silently kills it.
 * createPortal preserves React context and the child shares the parent JS heap, so
 * the detached viewer reads the same Zustand store as the grid on the main page —
 * the equivalent of Angular compiling the child DOM against the shared $rootScope.
 */

// Copy <link rel="stylesheet"> and <style> from the parent into the child head so the
// viewer looks identical in the detached window (childWindow.ts:48).
function copyStyles(source: Document, target: Document): void {
  source.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    target.head.appendChild(target.importNode(node, true));
  });
}

/**
 * Open and prepare the detached window. Call this directly from the click handler so
 * the browser treats it as user-initiated. Returns null if the popup was blocked.
 */
export function openDetachedWindow(): Window | null {
  const win = window.open('', '_blank', 'menubar=no,status=no,toolbar=no,location=no,fullscreen=yes');
  if (!win) return null;
  win.document.title = 'Document Viewer';
  copyStyles(document, win.document);
  win.document.body.style.margin = '0';
  return win;
}

interface Props {
  win: Window;
  /** Called when the user closes the detached window directly (native close). */
  onNativeClose: () => void;
  children: ReactNode;
}

export default function DetachedViewer({ win, onNativeClose, children }: Props) {
  useEffect(() => {
    // Native window close (X) pops the viewer back inline. We only listen; the window
    // lifecycle (close on pop-in) is owned by the store so this effect's cleanup does
    // not close the window — which keeps it StrictMode-safe (mount/cleanup/mount).
    win.addEventListener('pagehide', onNativeClose);
    // `pagehide` alone is not reliable for a user-initiated popup close in Chrome, so
    // poll `closed` as a fallback (childWindow.ts does the same) — otherwise closing the
    // popup via its own window controls leaves `detached` stuck true with no live window.
    const poll = window.setInterval(() => {
      if (win.closed) {
        window.clearInterval(poll);
        onNativeClose();
      }
    }, 500);
    // Parent page unload should take the child with it.
    const closeChild = () => { try { win.close(); } catch { /* noop */ } };
    window.addEventListener('pagehide', closeChild);
    return () => {
      window.clearInterval(poll);
      win.removeEventListener('pagehide', onNativeClose);
      window.removeEventListener('pagehide', closeChild);
    };
  }, [win, onNativeClose]);

  return createPortal(children, win.document.body);
}
